import type { FC, ReactNode } from 'react'

import { ArrowRight, Phone } from 'lucide-react'
import ButtonLink from '@/components/site/button-link'
import ParallaxPhoto from '@/components/site/parallax-photo'

import { BUSINESS } from '@/content/business'
import { getImage } from '@/content/images'

interface CtaBandProps {
    title?: string
    text?: string
}

const CtaBand: FC<CtaBandProps> = ({
    title = "Let's get your home looking its best.",
    text = "Tell us what you're picturing. We'll come out, measure up, and give you a free written estimate, so you know exactly what to expect before any work begins."
}): ReactNode => {
    return (
        <section aria-label='Get a free estimate' className='mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8'>
            <div className='grid overflow-hidden rounded-[2rem] bg-navy text-white lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]' data-reveal>
                <ParallaxPhoto
                    image={getImage('illustration-installer')}
                    className='min-h-64 rounded-none lg:min-h-full'
                    sizes='(min-width: 1024px) 45vw, 100vw'
                />

                <div className='@container p-8 sm:p-12'>
                    <h2 className='text-3xl font-bold text-white sm:text-4xl'>
                        {title}
                    </h2>

                    <p className='mt-4 text-lg text-white/90'>
                        {text}
                    </p>

                    <ul className='mt-5 space-y-1 text-base text-white/85'>
                        <li>Free measuring and written estimate</li>
                        <li>No pressure to sign, ever</li>
                        <li>{BUSINESS.licenseLabel}, licensed and insured</li>
                    </ul>

                    <div className='mt-8 grid max-w-xl gap-3 @[36rem]:grid-cols-2'>
                        <ButtonLink href={BUSINESS.phoneHref} variant='secondary' size='lg' className='w-full whitespace-nowrap px-5'>
                            <Phone className='size-5' aria-hidden='true' />
                            Call {BUSINESS.phone}
                        </ButtonLink>

                        <ButtonLink href='/contact/' variant='ghost-light' size='lg' className='w-full whitespace-nowrap px-5'>
                            Get a free estimate
                            <ArrowRight className='size-5 transition-transform group-hover:translate-x-1' aria-hidden='true' />
                        </ButtonLink>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default CtaBand
